import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Card,
  Input,
  Button,
  Tag,
  Tooltip,
  Select as AntSelect,
} from "antd";
import {
  SearchOutlined,
  EyeOutlined,
  ReloadOutlined,
  GiftOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  SyncOutlined,
  RocketOutlined,
  CalendarOutlined,
} from "@ant-design/icons";
import Pagination from "@mui/material/Pagination";
import axios from "axios";
import { API_BASE_URL } from "../../config";

const PAGE_SIZE = 10;

const statCard = (icon, label, value, color, bg) => (
  <div
    style={{
      background: bg,
      borderRadius: 14,
      padding: "20px 24px",
      display: "flex",
      alignItems: "center",
      gap: 16,
      boxShadow: "0 2px 12px rgba(0,0,0,0.07)",
      flex: 1,
      minWidth: 180,
    }}
  >
    <div
      style={{
        width: 52,
        height: 52,
        borderRadius: 12,
        background: color,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: 22,
        color: "#fff",
        flexShrink: 0,
      }}
    >
      {icon}
    </div>
    <div>
      <div style={{ fontSize: 13, color: "#64748b", marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: 26, fontWeight: 700, color: "#1e293b", lineHeight: 1.2 }}>{value}</div>
    </div>
  </div>
);

const statusTag = (status) => {
  const s = status?.toLowerCase();
  const map = {
    active: { color: "#0891b2", bg: "#e0f2fe", label: "Active Trial", icon: <SyncOutlined spin /> },
    expired: { color: "#dc2626", bg: "#fee2e2", label: "Expired", icon: <CloseCircleOutlined /> },
    converted: { color: "#16a34a", bg: "#dcfce7", label: "Converted", icon: <CheckCircleOutlined /> },
  };
  const cfg = map[s] || map.active;
  return (
    <span
      style={{
        padding: "3px 10px",
        borderRadius: 20,
        fontSize: 12,
        fontWeight: 600,
        color: cfg.color,
        background: cfg.bg,
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
      }}
    >
      {cfg.icon} {cfg.label}
    </span>
  );
};

const daysLeftBadge = (endDate) => {
  if (!endDate) return null;
  const days = Math.ceil((new Date(endDate) - new Date()) / 86400000);
  if (days < 0) return <span style={{ color: "#dc2626", fontSize: 11, fontWeight: 600 }}>Expired</span>;
  if (days <= 2)
    return (
      <span style={{ color: "#f59e0b", fontSize: 11, fontWeight: 600 }}>
        {days}d left ⚠️
      </span>
    );
  return <span style={{ color: "#64748b", fontSize: 11 }}>{days}d left</span>;
};

const FreeTrialUsers = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem("cms_token");

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const [stats, setStats] = useState({ total: 0, active: 0, expired: 0, converted: 0 });

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/get-user-list`, {
        params: {
          page,
          limit: PAGE_SIZE,
          search,
          subscription_type: "free_trial",
          status: statusFilter || undefined,
        },
        headers: { Authorization: `Bearer ${token}` },
      });
      const d = res.data;
      if (d.status === 200 || d.status === 201 || d.success) {
        const list = d.data || d.users || [];
        setUsers(list);
        setTotal(d.total || d.pagination?.total || list.length);
        setStats({
          total: d.total || list.length,
          active: d.active_count || list.filter((u) => u.subscription_status === "active").length,
          expired: d.expired_count || list.filter((u) => u.subscription_status === "expired").length,
          converted: d.converted_count || 0,
        });
      } else {
        setUsers([]);
      }
    } catch {
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, [page, search, statusFilter, token]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  useEffect(() => {
    setPage(1);
  }, [search, statusFilter]);

  const handleSearch = () => setSearch(searchInput);
  const handleReset = () => {
    setSearchInput("");
    setSearch("");
    setStatusFilter("");
    setPage(1);
  };

  const cols = ["#", "Customer", "Email", "Trial Plan", "Start Date", "End Date", "Days Left", "Status", "Actions"];

  return (
    <div className="main-content">
      <div className="page-content">
        <div className="container-fluid">
          {/* Header */}
          <div className="row mb-3">
            <div className="col-12">
              <div className="page-title-box d-sm-flex align-items-center justify-content-between">
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <div
                    style={{
                      width: 40,
                      height: 40,
                      borderRadius: 10,
                      background: "linear-gradient(135deg,#0891b2,#0e7490)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#fff",
                      fontSize: 18,
                    }}
                  >
                    <GiftOutlined />
                  </div>
                  <div>
                    <h4 style={{ margin: 0, fontWeight: 700, fontSize: 18, color: "#1e293b" }}>
                      Free Trial Users
                    </h4>
                    <p style={{ margin: 0, fontSize: 12, color: "#94a3b8" }}>
                      Customers currently on a free trial plan
                    </p>
                  </div>
                </div>
                <ol className="breadcrumb m-0">
                  <li className="breadcrumb-item">
                    <Link to="/cms/home">CMS</Link>
                  </li>
                  <li className="breadcrumb-item active">Free Trial Users</li>
                </ol>
              </div>
            </div>
          </div>

          {/* Stats */}
          <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginBottom: 24 }}>
            {statCard(<GiftOutlined />, "Total Free Trial", stats.total, "linear-gradient(135deg,#0891b2,#0e7490)", "#e0f2fe")}
            {statCard(<SyncOutlined />, "Active Trials", stats.active, "linear-gradient(135deg,#22c55e,#16a34a)", "#f0fdf4")}
            {statCard(<CloseCircleOutlined />, "Expired Trials", stats.expired, "linear-gradient(135deg,#f87171,#dc2626)", "#fef2f2")}
            {statCard(<RocketOutlined />, "Converted", stats.converted, "linear-gradient(135deg,#6366f1,#4f46e5)", "#eef2ff")}
          </div>

          {/* Conversion hint */}
          <div
            style={{
              background: "linear-gradient(135deg,#e0f2fe,#bae6fd)",
              borderRadius: 12,
              padding: "14px 20px",
              marginBottom: 20,
              display: "flex",
              alignItems: "center",
              gap: 12,
              border: "1px solid #bae6fd",
            }}
          >
            <RocketOutlined style={{ fontSize: 20, color: "#0891b2" }} />
            <div>
              <b style={{ color: "#0e7490" }}>Tip:</b>{" "}
              <span style={{ color: "#164e63", fontSize: 13 }}>
                Users with trials expiring in 2 days are highlighted — perfect time to reach out and convert them to paid plans.
              </span>
            </div>
          </div>

          {/* Table card */}
          <Card
            style={{ borderRadius: 14, boxShadow: "0 2px 12px rgba(0,0,0,0.07)" }}
            bodyStyle={{ padding: 0 }}
          >
            {/* Toolbar */}
            <div
              style={{
                padding: "16px 20px",
                borderBottom: "1px solid #f1f5f9",
                display: "flex",
                alignItems: "center",
                gap: 10,
                flexWrap: "wrap",
              }}
            >
              <Input
                prefix={<SearchOutlined style={{ color: "#94a3b8" }} />}
                placeholder="Search by name or email..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onPressEnter={handleSearch}
                style={{ width: 260, borderRadius: 8 }}
                allowClear
                onClear={() => { setSearchInput(""); setSearch(""); }}
              />
              <AntSelect
                placeholder="All Statuses"
                value={statusFilter || undefined}
                onChange={(v) => setStatusFilter(v || "")}
                allowClear
                style={{ width: 160, borderRadius: 8 }}
                options={[
                  { value: "active", label: "Active Trial" },
                  { value: "expired", label: "Expired" },
                  { value: "converted", label: "Converted" },
                ]}
              />
              <Button
                icon={<SearchOutlined />}
                onClick={handleSearch}
                style={{
                  background: "linear-gradient(135deg,#0891b2,#0e7490)",
                  color: "#fff",
                  border: "none",
                  borderRadius: 8,
                  fontWeight: 600,
                }}
              >
                Search
              </Button>
              <Button icon={<ReloadOutlined />} onClick={handleReset} style={{ borderRadius: 8 }}>
                Reset
              </Button>
              <div style={{ marginLeft: "auto", color: "#64748b", fontSize: 13 }}>
                Total: <b>{total}</b> free trial users
              </div>
            </div>

            {/* Table */}
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                <thead>
                  <tr style={{ background: "#f8fafc" }}>
                    {cols.map((c) => (
                      <th
                        key={c}
                        style={{
                          padding: "12px 14px",
                          textAlign: "left",
                          fontWeight: 600,
                          color: "#475569",
                          fontSize: 12,
                          borderBottom: "1px solid #e2e8f0",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {c}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={9} style={{ textAlign: "center", padding: 48, color: "#94a3b8" }}>
                        Loading...
                      </td>
                    </tr>
                  ) : users.length === 0 ? (
                    <tr>
                      <td colSpan={9} style={{ textAlign: "center", padding: 48 }}>
                        <div style={{ color: "#94a3b8", fontSize: 15 }}>
                          <GiftOutlined style={{ fontSize: 36, marginBottom: 12, display: "block" }} />
                          No free trial users found
                        </div>
                      </td>
                    </tr>
                  ) : (
                    users.map((user, idx) => {
                      const endDate = user.subscription_end_date || user.end_date;
                      return (
                        <tr
                          key={user.id || idx}
                          style={{ borderBottom: "1px solid #f1f5f9" }}
                          onMouseEnter={(e) => (e.currentTarget.style.background = "#f8fafc")}
                          onMouseLeave={(e) => (e.currentTarget.style.background = "")}
                        >
                          <td style={{ padding: "12px 14px", color: "#64748b" }}>
                            {(page - 1) * PAGE_SIZE + idx + 1}
                          </td>
                          <td style={{ padding: "12px 14px" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                              <div
                                style={{
                                  width: 34,
                                  height: 34,
                                  borderRadius: "50%",
                                  background: "linear-gradient(135deg,#0891b2,#0e7490)",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  color: "#fff",
                                  fontWeight: 700,
                                  fontSize: 13,
                                  flexShrink: 0,
                                }}
                              >
                                {(user.name || user.full_name || "U")[0]?.toUpperCase()}
                              </div>
                              <div>
                                <div style={{ fontWeight: 600, color: "#1e293b" }}>
                                  {user.name || user.full_name || "—"}
                                </div>
                                <div style={{ fontSize: 11, color: "#94a3b8" }}>ID: {user.id}</div>
                              </div>
                            </div>
                          </td>
                          <td style={{ padding: "12px 14px", color: "#475569" }}>{user.email || "—"}</td>
                          <td style={{ padding: "12px 14px" }}>
                            <Tag color="cyan" style={{ borderRadius: 6, fontWeight: 600 }}>
                              {user.plan_name || "Free Trial"}
                            </Tag>
                          </td>
                          <td style={{ padding: "12px 14px", color: "#475569" }}>
                            {user.subscription_start_date || user.start_date || "—"}
                          </td>
                          <td style={{ padding: "12px 14px", color: "#475569" }}>{endDate || "—"}</td>
                          <td style={{ padding: "12px 14px" }}>{daysLeftBadge(endDate)}</td>
                          <td style={{ padding: "12px 14px" }}>
                            {statusTag(user.subscription_status || (user.status === 1 ? "active" : "expired"))}
                          </td>
                          <td style={{ padding: "12px 14px" }}>
                            <Tooltip title="View Profile">
                              <button
                                onClick={() => navigate(`/cms/manageUserData/user-profile/${user.id}`)}
                                style={{
                                  background: "#e0f2fe",
                                  border: "none",
                                  borderRadius: 7,
                                  width: 32,
                                  height: 32,
                                  cursor: "pointer",
                                  color: "#0891b2",
                                  display: "inline-flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  fontSize: 14,
                                }}
                              >
                                <EyeOutlined />
                              </button>
                            </Tooltip>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {total > PAGE_SIZE && (
              <div
                style={{
                  padding: "16px 24px",
                  borderTop: "1px solid #f0f0f0",
                  display: "flex",
                  justifyContent: "center",
                  background: "#fafbff",
                }}
              >
                <Pagination
                  count={Math.ceil(total / PAGE_SIZE)}
                  page={page}
                  onChange={(_, v) => setPage(v)}
                  color="primary"
                />
              </div>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
};

export default FreeTrialUsers;
